import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LOGIN, REGISTER } from "@/constants/testIds";
import SocialLogin from "@/components/SocialLogin";

export function Login() {
  const { acceptSession } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await api.post("/auth/login", { email, password });
      acceptSession(response.data);
      nav(response.data.user.entitled ? "/" : "/paywall");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-5 pt-12">
      <p className="text-xs tracking-[0.18em] uppercase font-bold text-orange-600">Job Spoken</p>
      <h1 className="font-heading text-3xl font-black tracking-tight mt-2">Sign in</h1>
      <p className="text-sm text-zinc-500 mt-1">Speak the job. Send the bill. Get paid on-site.</p>
      <form onSubmit={submit} className="mt-8 space-y-3">
        <Input data-testid={LOGIN.emailInput} type="email" autoComplete="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" />
        <Input data-testid={LOGIN.passwordInput} type="password" autoComplete="current-password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" />
        <Button data-testid={LOGIN.submitButton} disabled={busy} className="w-full h-12 font-bold bg-orange-600 hover:bg-orange-700">{busy ? "Signing in…" : "Sign in"}</Button>
      </form>
      <div className="flex justify-between text-sm mt-3">
        <Link data-testid={LOGIN.forgotPasswordLink} to="/forgot" className="text-zinc-500 font-medium">Forgot password</Link>
        <Link data-testid={LOGIN.registerLink} to="/register" className="text-orange-600 font-bold">Create account</Link>
      </div>
      <div className="mt-6"><SocialLogin onError={(message) => toast.error(message)} /></div>
    </div>
  );
}

export function Register() {
  const { acceptSession } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", founding_code: "" });
  const [busy, setBusy] = useState(false);
  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const submit = async (event) => {
    event.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Those passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const response = await api.post("/auth/register", {
        name: form.name, email: form.email, password: form.password, founding_code: form.founding_code,
      });
      acceptSession(response.data);
      nav("/onboarding");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create the account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-5 pt-12 pb-10">
      <p className="text-xs tracking-[0.18em] uppercase font-bold text-orange-600">Job Spoken</p>
      <h1 className="font-heading text-3xl font-black tracking-tight mt-2">Create your account</h1>
      <p className="text-sm text-zinc-500 mt-1">14 days free. No card required to start.</p>
      <form onSubmit={submit} className="mt-8 space-y-3">
        <Input data-testid={REGISTER.nameInput} required placeholder="Your name" value={form.name} onChange={set("name")} className="h-12" />
        <Input data-testid={REGISTER.emailInput} type="email" autoComplete="email" required placeholder="Email" value={form.email} onChange={set("email")} className="h-12" />
        <Input data-testid={REGISTER.passwordInput} type="password" autoComplete="new-password" required placeholder="Password (8+ characters)" value={form.password} onChange={set("password")} className="h-12" />
        <Input data-testid={REGISTER.passwordConfirmInput} type="password" required placeholder="Confirm password" value={form.confirm} onChange={set("confirm")} className="h-12" />
        <Input data-testid="register-founding-code" placeholder="Founding member code (optional)" value={form.founding_code} onChange={set("founding_code")} className="h-12" />
        <Button data-testid={REGISTER.submitButton} disabled={busy} className="w-full h-12 font-bold bg-orange-600 hover:bg-orange-700">{busy ? "Creating…" : "Create account"}</Button>
      </form>
      <p className="text-sm text-center mt-4">
        <Link data-testid={REGISTER.loginLink} to="/login" className="text-orange-600 font-bold">I already have an account</Link>
      </p>
      <div className="mt-6"><SocialLogin onError={(message) => toast.error(message)} /></div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email });
      setSent(true);
    } catch {
      toast.error("Could not send a reset email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-5 pt-12">
      <h1 className="font-heading text-3xl font-black tracking-tight">Reset password</h1>
      <p className="text-sm text-zinc-500 mt-1">We'll email a link if an account exists for that address.</p>
      {sent ? (
        <p data-testid="forgot-sent" className="mt-8 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-xl p-4">Check your inbox for a reset link. It expires in one hour.</p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-3">
          <Input data-testid="forgot-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" />
          <Button data-testid="forgot-submit-button" disabled={busy} className="w-full h-12 font-bold bg-orange-600 hover:bg-orange-700">Send reset link</Button>
        </form>
      )}
      <Link to="/login" className="block text-sm text-orange-600 font-bold mt-4">Back to sign in</Link>
    </div>
  );
}

export function ResetPassword() {
  const { acceptSession } = useAuth();
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await api.post("/auth/reset", { token, password });
      acceptSession(response.data);
      toast.success("Password updated");
      nav("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "This reset link is no longer valid.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-5 pt-12">
      <h1 className="font-heading text-3xl font-black tracking-tight">Choose a new password</h1>
      <form onSubmit={submit} className="mt-8 space-y-3">
        <Input data-testid="reset-password-input" type="password" required minLength={8} placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" />
        <Button data-testid="reset-submit-button" disabled={busy || !token} className="w-full h-12 font-bold bg-orange-600 hover:bg-orange-700">Update password</Button>
      </form>
      {!token && <p className="text-sm text-red-600 mt-3">This page needs the link from your email.</p>}
    </div>
  );
}
